import React, { useState, useEffect } from 'react';
import { Plus, TrendingUp, TrendingDown, DollarSign, PieChart, FileText, Upload, Download, RefreshCw, LogOut, Pencil, Trash2, Layers, Activity, BarChart3, Newspaper } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import TransactionTable from '../components/TransactionTable';
import AddTransactionModal from '../components/AddTransactionModal';
import HoldingsReport from '../components/HoldingsReport';
import CapitalGainsReport from '../components/CapitalGainsReport';
import DailySummary from '../components/DailySummary';
import StockNews from '../components/StockNews';
import ExcelUpload from '../components/ExcelUpload';
import PortfolioModal from '../components/PortfolioModal';
import { exportTransactionsToExcel, exportHoldingsToExcel, exportCompleteReport, exportCapitalGainsToExcel, exportDailySummaryToExcel } from '../utils/excelExport';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatINR = (n) => (n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const isMarketOpenIST = () => {
  const ist = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const day = ist.getDay();
  if (day === 0 || day === 6) return false;
  const mins = ist.getHours() * 60 + ist.getMinutes();
  return mins >= 9 * 60 + 15 && mins <= 16 * 60;
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();

  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState('all');
  const [transactions, setTransactions] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExcelUploadOpen, setIsExcelUploadOpen] = useState(false);
  const [isPortfolioModalOpen, setIsPortfolioModalOpen] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showHoldingsReport, setShowHoldingsReport] = useState(false);
  const [showCapitalGainsReport, setShowCapitalGainsReport] = useState(false);
  const [showDailySummary, setShowDailySummary] = useState(false);
  const [showNews, setShowNews] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [portfolioSummary, setPortfolioSummary] = useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastPriceUpdate, setLastPriceUpdate] = useState(null);
  const [marketOpen, setMarketOpen] = useState(isMarketOpenIST());

  const loadPortfolios = async () => {
    const res = await axios.get(`${API}/portfolios`);
    setPortfolios(res.data);
    return res.data;
  };

  const loadTransactions = async (pid) => {
    const [txnRes, sumRes] = await Promise.all([
      axios.get(`${API}/transactions`, { params: { portfolioId: pid || 'all' } }),
      axios.get(`${API}/portfolio/summary`, { params: { portfolioId: pid || 'all' } }),
    ]);
    setTransactions(txnRes.data);
    setPortfolioSummary(sumRes.data);
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadPortfolios();
      } catch (e) {
        toast({ title: 'Error', description: 'Failed to load portfolios', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading) {
      loadTransactions(selectedPortfolioId).catch(() => {});
    }
    setShowHoldingsReport(false);
    setShowCapitalGainsReport(false);
    setShowDailySummary(false);
    setShowNews(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortfolioId, loading]);

  // Market-status badge updater (every minute)
  useEffect(() => {
    const t = setInterval(() => setMarketOpen(isMarketOpenIST()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-refresh: re-fetch transactions every 15 min during market hours so the
  // dashboard reflects the backend scheduler's live price updates.
  useEffect(() => {
    const poll = setInterval(() => {
      if (!loading && isMarketOpenIST()) {
        loadTransactions(selectedPortfolioId)
          .then(() => setLastPriceUpdate(new Date()))
          .catch(() => {});
      }
    }, 15 * 60 * 1000);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortfolioId, loading]);

  const selectedPortfolio = portfolios.find((p) => p.id === selectedPortfolioId);
  const hasPortfolios = portfolios.length > 0;
  const targetPortfolioId = selectedPortfolioId !== 'all' ? selectedPortfolioId : (portfolios[0]?.id || '');

  const entityInfo = selectedPortfolioId === 'all'
    ? { entityName: 'All Portfolios (Combined)', entityType: user?.name || 'Investor', panNumber: '—', portfolioId: 'ALL' }
    : selectedPortfolio
      ? { entityName: selectedPortfolio.name, entityType: selectedPortfolio.entityType, panNumber: selectedPortfolio.panNumber || '—', portfolioId: selectedPortfolio.code }
      : null;

  // ---- Transaction handlers ----
  const handleAddTransaction = async (newTransaction) => {
    try {
      if (editingTransaction) {
        const qSold = newTransaction.quantitySold ?? newTransaction.quantity;
        const isPartial = newTransaction.status === 'sold' && qSold > 0 && qSold < editingTransaction.quantity;
        if (isPartial) {
          await axios.post(`${API}/transactions/${editingTransaction.id}/sell`, {
            quantitySold: qSold,
            sellPrice: newTransaction.sellPrice,
            sellDate: newTransaction.sellDate,
          });
          toast({ title: 'Partial Sale Recorded', description: `Sold ${qSold} unit(s); remaining kept as held.` });
        } else {
          await axios.put(`${API}/transactions/${editingTransaction.id}`, newTransaction);
          toast({ title: 'Success', description: 'Transaction updated successfully' });
        }
        setEditingTransaction(null);
      } else {
        await axios.post(`${API}/transactions`, newTransaction);
        toast({ title: 'Success', description: 'Transaction added successfully' });
      }
      await loadTransactions(selectedPortfolioId);
      setIsModalOpen(false);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save transaction', variant: 'destructive' });
    }
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setIsModalOpen(true);
  };

  const handleDeleteTransaction = async (id) => {
    try {
      await axios.delete(`${API}/transactions/${id}`);
      setTransactions(transactions.filter((t) => t.id !== id));
      toast({ title: 'Success', description: 'Transaction deleted successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete transaction', variant: 'destructive' });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTransaction(null);
  };

  const handleOpenAddTransaction = () => {
    if (!hasPortfolios) {
      toast({ title: 'Create a portfolio first', description: 'Add a portfolio before recording transactions.', variant: 'destructive' });
      return;
    }
    setIsModalOpen(true);
  };

  const handleRefreshPrices = async () => {
    try {
      setRefreshing(true);
      const response = await axios.post(`${API}/transactions/refresh-prices`, null, { params: { portfolioId: selectedPortfolioId } });
      const { updated, failed } = response.data;
      await loadTransactions(selectedPortfolioId);
      setLastPriceUpdate(new Date());
      if (updated > 0) {
        toast({ title: 'Live Prices Updated', description: `Updated ${updated} held position(s) from NSE/BSE${failed.length ? ` • Couldn't fetch ${failed.length}` : ''}` });
      } else {
        toast({ title: 'No Updates', description: failed.length ? `Couldn't fetch prices for ${failed.join(', ')}.` : 'No held positions to update.', variant: failed.length ? 'destructive' : 'default' });
      }
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to fetch live prices', variant: 'destructive' });
    } finally {
      setRefreshing(false);
    }
  };

  const handleEmailSummary = async () => {
    try {
      setEmailSending(true);
      const response = await axios.post(`${API}/portfolio/email-summary`, null, { params: { portfolioId: selectedPortfolioId } });
      toast({ title: 'Summary Emailed', description: `Sent to ${response.data.to}` });
    } catch (error) {
      toast({ title: 'Could not send email', description: error.response?.data?.detail || 'Failed to send summary email', variant: 'destructive' });
    } finally {
      setEmailSending(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      toast({ title: 'Preparing report', description: 'Building your graphical PDF (fetching news may take a moment)…' });
      const res = await axios.get(`${API}/reports/daily-pdf`, { params: { portfolioId: selectedPortfolioId }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Daily_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Report ready', description: 'Your graphical PDF has been downloaded.' });
    } catch (error) {
      toast({ title: 'Could not generate report', description: 'Please try again in a moment.', variant: 'destructive' });
    } finally {
      setDownloadingPdf(false);
    }
  };

  // ---- Portfolio handlers ----
  const handleSavePortfolio = async (data) => {
    try {
      if (editingPortfolio) {
        await axios.put(`${API}/portfolios/${editingPortfolio.id}`, data);
        toast({ title: 'Success', description: 'Portfolio updated' });
        await loadPortfolios();
      } else {
        const res = await axios.post(`${API}/portfolios`, data);
        toast({ title: 'Success', description: 'Portfolio created' });
        await loadPortfolios();
        setSelectedPortfolioId(res.data.id);
      }
      setIsPortfolioModalOpen(false);
      setEditingPortfolio(null);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save portfolio', variant: 'destructive' });
    }
  };

  const handleDeletePortfolio = async () => {
    if (!selectedPortfolio) return;
    if (!window.confirm(`Delete portfolio "${selectedPortfolio.name}" and ALL its transactions? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/portfolios/${selectedPortfolio.id}`);
      toast({ title: 'Deleted', description: 'Portfolio removed' });
      await loadPortfolios();
      setSelectedPortfolioId('all');
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete portfolio', variant: 'destructive' });
    }
  };

  // ---- Summary ---- (Total Investment & Current Value reflect HELD positions only, matching Holdings Report)
  const summary = transactions.reduce((acc, t) => {
    if (t.status === 'held') {
      acc.totalInvestment += t.totalCost || 0;
      acc.totalCurrentValue += t.currentValue || 0;
    }
    acc.totalProfit += t.profit || 0;
    acc.todaysGainLoss += (t.status === 'held' ? (t.dayChange || 0) : 0);
    if (t.status === 'sold') acc.totalRealized += t.profit || 0;
    else { acc.totalUnrealized += t.profit || 0; acc.heldCount += 1; }
    return acc;
  }, { totalInvestment: 0, totalCurrentValue: 0, totalProfit: 0, totalRealized: 0, totalUnrealized: 0, heldCount: 0, todaysGainLoss: 0 });

  const totalReturnPercentage = summary.totalInvestment > 0 ? ((summary.totalProfit / summary.totalInvestment) * 100).toFixed(2) : 0;
  const xirr = portfolioSummary?.xirr;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-green-50 flex items-center justify-center">
        <div className="text-2xl font-semibold text-slate-700">Loading portfolio...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-green-50">
      {/* Top nav */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-orange-600 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-lg text-slate-900">Equity Portfolio</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-slate-100 text-slate-600 flex items-center gap-1.5" data-testid="market-status-badge" title="Auto-refresh runs every 15 min during market hours (09:15-16:00 IST, Mon-Fri)">
              <span className={`h-2 w-2 rounded-full ${marketOpen ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`}></span>
              {marketOpen ? 'Market Open • Auto-refresh' : 'Market Closed'}
            </span>
            <span className="text-sm text-slate-600 hidden sm:inline" data-testid="user-name">Hi, {user?.name}</span>
            <Button onClick={logout} variant="outline" size="sm" className="border-slate-300 text-slate-700" data-testid="logout-btn">
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Portfolio selector + Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-orange-600" />
                <Select value={selectedPortfolioId} onValueChange={setSelectedPortfolioId}>
                  <SelectTrigger className="w-[260px] bg-white border-slate-300 font-semibold" data-testid="portfolio-selector">
                    <SelectValue placeholder="Select portfolio" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">📊 All Portfolios (Combined)</SelectItem>
                    {portfolios.map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`portfolio-option-${p.id}`}>{p.name} ({p.entityType})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => { setEditingPortfolio(null); setIsPortfolioModalOpen(true); }} variant="outline" size="sm" className="border-orange-600 text-orange-600 hover:bg-orange-50" data-testid="new-portfolio-btn">
                <Plus className="mr-1 h-4 w-4" /> New Portfolio
              </Button>
              {selectedPortfolio && (
                <>
                  <Button onClick={() => { setEditingPortfolio(selectedPortfolio); setIsPortfolioModalOpen(true); }} variant="ghost" size="sm" className="text-slate-600" data-testid="edit-portfolio-btn">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button onClick={handleDeletePortfolio} variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" data-testid="delete-portfolio-btn">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
            {entityInfo && (
              <div className="mt-3">
                <h1 className="text-3xl font-bold text-slate-900">{entityInfo.entityName}</h1>
                <p className="text-slate-600 mt-1 text-sm">{entityInfo.entityType} • {entityInfo.portfolioId !== 'ALL' ? `Code: ${entityInfo.portfolioId} • PAN: ${entityInfo.panNumber}` : `${portfolios.length} portfolio(s) combined`}</p>
                {lastPriceUpdate && (
                  <p className="text-xs text-blue-600 mt-1 flex items-center gap-1" data-testid="last-price-update">
                    <RefreshCw className="h-3 w-3" /> Live prices updated at {lastPriceUpdate.toLocaleTimeString('en-IN')}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button onClick={handleRefreshPrices} disabled={refreshing} className="bg-blue-600 hover:bg-blue-700 text-white shadow" data-testid="refresh-prices-btn">
              <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Fetching...' : 'Refresh Live Prices'}
            </Button>
            <div className="relative">
              <Button onClick={() => setShowExportMenu(!showExportMenu)} variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50" data-testid="download-reports-btn">
                <Download className="mr-2 h-4 w-4" /> Download Reports
              </Button>
              {showExportMenu && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-slate-200 z-50">
                  <div className="p-2">
                    <button onClick={() => { exportDailySummaryToExcel(transactions, entityInfo, portfolioSummary); setShowExportMenu(false); toast({ title: 'Success', description: 'Daily summary downloaded' }); }} className="w-full text-left px-4 py-2 hover:bg-slate-100 rounded-md text-sm" data-testid="export-daily-summary">
                      <div className="font-semibold text-slate-900">Daily Summary Report</div>
                      <div className="text-xs text-slate-600">Top 25 lists, gainers/losers, XIRR</div>
                    </button>
                    <button onClick={() => { exportCompleteReport(transactions, entityInfo); setShowExportMenu(false); toast({ title: 'Success', description: 'Complete report downloaded' }); }} className="w-full text-left px-4 py-2 hover:bg-slate-100 rounded-md text-sm" data-testid="export-complete">
                      <div className="font-semibold text-slate-900">Complete Portfolio Report</div>
                      <div className="text-xs text-slate-600">All data with summary</div>
                    </button>
                    <button onClick={() => { exportCapitalGainsToExcel(transactions, entityInfo, portfolios); setShowExportMenu(false); toast({ title: 'Success', description: 'Capital gains report downloaded' }); }} className="w-full text-left px-4 py-2 hover:bg-slate-100 rounded-md text-sm" data-testid="export-capital-gains">
                      <div className="font-semibold text-slate-900">Capital Gains Report</div>
                      <div className="text-xs text-slate-600">LTCG & STCG with tax info</div>
                    </button>
                    <button onClick={() => { exportHoldingsToExcel(transactions, entityInfo); setShowExportMenu(false); toast({ title: 'Success', description: 'Holdings report downloaded' }); }} className="w-full text-left px-4 py-2 hover:bg-slate-100 rounded-md text-sm" data-testid="export-holdings">
                      <div className="font-semibold text-slate-900">Holdings Report</div>
                      <div className="text-xs text-slate-600">Current holdings only</div>
                    </button>
                    <button onClick={() => { exportTransactionsToExcel(transactions, entityInfo, portfolios); setShowExportMenu(false); toast({ title: 'Success', description: 'Transactions exported' }); }} className="w-full text-left px-4 py-2 hover:bg-slate-100 rounded-md text-sm" data-testid="export-transactions">
                      <div className="font-semibold text-slate-900">All Transactions</div>
                      <div className="text-xs text-slate-600">Transaction history</div>
                    </button>
                    <div className="my-1 border-t border-slate-100"></div>
                    <button onClick={() => { setShowExportMenu(false); handleDownloadPdf(); }} disabled={downloadingPdf} className="w-full text-left px-4 py-2 hover:bg-rose-50 rounded-md text-sm disabled:opacity-60" data-testid="export-graphical-pdf">
                      <div className="font-semibold text-rose-700 flex items-center gap-2">{downloadingPdf ? <RefreshCw className="h-3 w-3 animate-spin" /> : null}Graphical PDF Report</div>
                      <div className="text-xs text-slate-600">Charts, gainers/losers, near 52W, news</div>
                    </button>
                  </div>
                </div>
              )}
            </div>
            <Button onClick={() => setShowDailySummary(!showDailySummary)} variant="outline" className="border-indigo-600 text-indigo-600 hover:bg-indigo-50" data-testid="view-daily-summary-btn">
              <BarChart3 className="mr-2 h-4 w-4" /> {showDailySummary ? 'Hide' : 'Daily Summary'}
            </Button>
            <Button onClick={() => setShowNews(!showNews)} variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50" data-testid="view-news-btn">
              <Newspaper className="mr-2 h-4 w-4" /> {showNews ? 'Hide' : 'Daily News'}
            </Button>
            <Button onClick={() => setShowHoldingsReport(!showHoldingsReport)} variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50" data-testid="view-holdings-btn">
              <FileText className="mr-2 h-4 w-4" /> {showHoldingsReport ? 'Hide' : 'Holdings'}
            </Button>
            <Button onClick={() => setShowCapitalGainsReport(!showCapitalGainsReport)} variant="outline" className="border-purple-600 text-purple-600 hover:bg-purple-50" data-testid="view-capital-gains-btn">
              <TrendingUp className="mr-2 h-4 w-4" /> {showCapitalGainsReport ? 'Hide' : 'Capital Gains'}
            </Button>
            <Button onClick={() => setIsExcelUploadOpen(true)} variant="outline" className="border-green-600 text-green-600 hover:bg-green-50" data-testid="upload-excel-btn">
              <Upload className="mr-2 h-4 w-4" /> Upload Excel
            </Button>
            <Button onClick={handleOpenAddTransaction} className="bg-orange-600 hover:bg-orange-700 text-white shadow" data-testid="add-transaction-btn">
              <Plus className="mr-2 h-4 w-4" /> Add Transaction
            </Button>
          </div>
        </div>

        {!hasPortfolios ? (
          <Card className="border-2 border-dashed border-orange-300 bg-white">
            <CardContent className="py-16 text-center">
              <Layers className="h-12 w-12 text-orange-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900">Create your first portfolio</h3>
              <p className="text-slate-600 mt-2 mb-6">Organize investments by investor — e.g. Self, Spouse, or HUF.</p>
              <Button onClick={() => { setEditingPortfolio(null); setIsPortfolioModalOpen(true); }} className="bg-orange-600 hover:bg-orange-700 text-white" size="lg" data-testid="create-first-portfolio-btn">
                <Plus className="mr-2 h-5 w-5" /> New Portfolio
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <Card className="border-none shadow-lg bg-white"><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium text-slate-600">Total Investment</CardTitle><DollarSign className="h-5 w-5 text-slate-400" /></CardHeader><CardContent><div className="text-2xl font-bold text-slate-900" data-testid="summary-investment">₹{formatINR(summary.totalInvestment)}</div><p className="text-xs text-slate-500 mt-1">{transactions.length} transactions</p></CardContent></Card>
              <Card className="border-none shadow-lg bg-white"><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium text-slate-600">Current Value</CardTitle><TrendingUp className="h-5 w-5 text-blue-500" /></CardHeader><CardContent><div className="text-2xl font-bold text-blue-600">₹{formatINR(summary.totalCurrentValue)}</div><p className="text-xs text-slate-500 mt-1">{summary.heldCount} held</p></CardContent></Card>
              <Card className="border-none shadow-lg bg-white"><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium text-slate-600">Total Profit/Loss</CardTitle>{summary.totalProfit >= 0 ? <TrendingUp className="h-5 w-5 text-emerald-500" /> : <TrendingDown className="h-5 w-5 text-rose-500" />}</CardHeader><CardContent><div className={`text-2xl font-bold ${summary.totalProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>₹{formatINR(summary.totalProfit)}</div><p className="text-xs text-slate-500 mt-1"><span className={summary.totalProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}>{totalReturnPercentage > 0 ? '+' : ''}{totalReturnPercentage}%</span> return</p></CardContent></Card>
              <Card className="border-none shadow-lg bg-white" data-testid="card-todays-gl"><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium text-slate-600">Today's Gain/Loss</CardTitle><Activity className="h-5 w-5 text-amber-500" /></CardHeader><CardContent><div className={`text-2xl font-bold ${summary.todaysGainLoss >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{summary.todaysGainLoss >= 0 ? '+' : ''}₹{formatINR(summary.todaysGainLoss)}</div><p className="text-xs text-slate-500 mt-1">{portfolioSummary?.todaysGainLossPercent != null ? `${portfolioSummary.todaysGainLossPercent > 0 ? '+' : ''}${portfolioSummary.todaysGainLossPercent}% today` : 'Refresh for live'}</p></CardContent></Card>
              <Card className="border-none shadow-lg bg-white"><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium text-slate-600">Unrealized P/L</CardTitle><PieChart className="h-5 w-5 text-orange-400" /></CardHeader><CardContent><div className={`text-2xl font-bold ${summary.totalUnrealized >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>₹{formatINR(summary.totalUnrealized)}</div><p className="text-xs text-slate-500 mt-1">Held positions</p></CardContent></Card>
              <Card className="border-none shadow-lg bg-white" data-testid="card-xirr"><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium text-slate-600">Portfolio CAGR</CardTitle><BarChart3 className="h-5 w-5 text-indigo-500" /></CardHeader><CardContent><div className={`text-2xl font-bold ${(xirr || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{xirr != null ? `${xirr > 0 ? '+' : ''}${xirr}%` : 'N/A'}</div><p className="text-xs text-slate-500 mt-1">XIRR (annualized)</p></CardContent></Card>
            </div>

            {showDailySummary && (
              <DailySummary transactions={transactions} entityInfo={entityInfo} summary={portfolioSummary} onEmail={handleEmailSummary} emailSending={emailSending} portfolioId={selectedPortfolioId} onEnriched={() => loadTransactions(selectedPortfolioId).catch(() => {})} />
            )}
            {showNews && (
              <StockNews portfolioId={selectedPortfolioId} />
            )}
            {showHoldingsReport && (
              <HoldingsReport transactions={transactions.filter((t) => t.status === 'held')} entityInfo={entityInfo} portfolioId={selectedPortfolioId} onEnriched={() => loadTransactions(selectedPortfolioId).catch(() => {})} />
            )}
            {showCapitalGainsReport && (
              <CapitalGainsReport transactions={transactions} entityInfo={entityInfo} portfolios={portfolios} />
            )}

            <Card className="border-none shadow-lg bg-white">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-slate-900">Transaction History</CardTitle>
              </CardHeader>
              <CardContent>
                <TransactionTable transactions={transactions} onEdit={handleEditTransaction} onDelete={handleDeleteTransaction} />
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <AddTransactionModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleAddTransaction}
        editingTransaction={editingTransaction}
        portfolios={portfolios}
        defaultPortfolioId={targetPortfolioId}
      />

      <ExcelUpload
        isOpen={isExcelUploadOpen}
        onClose={() => setIsExcelUploadOpen(false)}
        onSuccess={async () => { await loadPortfolios(); await loadTransactions(selectedPortfolioId); }}
        portfolios={portfolios}
        defaultPortfolioId={targetPortfolioId}
      />

      <PortfolioModal
        isOpen={isPortfolioModalOpen}
        onClose={() => { setIsPortfolioModalOpen(false); setEditingPortfolio(null); }}
        onSubmit={handleSavePortfolio}
        editingPortfolio={editingPortfolio}
      />
    </div>
  );
};

export default Dashboard;
